"use client";

import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, X, File as FileIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CustomQuestion } from "@/services/types";
import { useUploadFileMutation } from "@/services/files/queries";
import { showToast } from "@/components/ui/show-toast";
import { usePostHog } from "posthog-js/react";
// import type { CustomQuestion } from "./types";

interface SignifyInterestStepTwoProps {
  customQuestions: CustomQuestion[];
  onSubmit: (data: Record<string, any>) => void;
  onBack: () => void;
}

// Generate zod schema dynamically from custom questions
const generateSchema = (questions: CustomQuestion[]) => {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  questions.forEach((question) => {
    let fieldSchema: z.ZodTypeAny;

    switch (question.type) {
      case "singleselect":
      case "multiselect":
        if (question.type === "multiselect") {
          fieldSchema = z
            .array(z.string())
            .min(1, `${question.label} is required`);
        } else {
          fieldSchema = z.string().min(1, `${question.label} is required`);
        }
        break;
      case "url":
        if (question.required) {
          fieldSchema = z
            .string()
            .url("Please enter a valid URL")
            .min(1, `${question.label} is required`);
        } else {
          fieldSchema = z
            .string()
            .url("Please enter a valid URL")
            .optional()
            .or(z.literal(""));
        }
        break;
      case "file":
        // File type stores an array of URLs after upload
        if (question.required) {
          fieldSchema = z
            .array(z.string().url())
            .min(1, `${question.label} is required`);
        } else {
          fieldSchema = z.array(z.string().url()).optional();
        }
        break;
      case "number":
        if (question.required) {
          let numberSchema = z.number();
          if (question.numberConfig?.min !== undefined) {
            numberSchema = numberSchema.min(
              question.numberConfig.min,
              `Minimum value is ${question.numberConfig.min}`
            );
          }
          if (question.numberConfig?.max !== undefined) {
            numberSchema = numberSchema.max(
              question.numberConfig.max,
              `Maximum value is ${question.numberConfig.max}`
            );
          }
          numberSchema = numberSchema.min(1, `${question.label} is required`);
          fieldSchema = numberSchema;
        } else {
          let numberSchema = z.number();
          if (question.numberConfig?.min !== undefined) {
            numberSchema = numberSchema.min(
              question.numberConfig.min,
              `Minimum value is ${question.numberConfig.min}`
            );
          }
          if (question.numberConfig?.max !== undefined) {
            numberSchema = numberSchema.max(
              question.numberConfig.max,
              `Maximum value is ${question.numberConfig.max}`
            );
          }
          fieldSchema = numberSchema.optional();
        }
        break;
      case "text":
        if (question.required) {
          let textSchema = z.string().min(1, `${question.label} is required`);
          if (question.textConfig?.maxLength) {
            textSchema = textSchema.max(
              question.textConfig.maxLength,
              `Maximum length is ${question.textConfig.maxLength} characters`
            );
          }
          fieldSchema = textSchema;
        } else {
          let textSchema = z.string();
          if (question.textConfig?.maxLength) {
            textSchema = textSchema.max(
              question.textConfig.maxLength,
              `Maximum length is ${question.textConfig.maxLength} characters`
            );
          }
          fieldSchema = textSchema.optional().or(z.literal(""));
        }
        break;
      case "textarea":
        if (question.required) {
          let textareaSchema = z
            .string()
            .min(1, `${question.label} is required`);
          if (question.textConfig?.maxLength) {
            textareaSchema = textareaSchema.max(
              question.textConfig.maxLength,
              `Maximum length is ${question.textConfig.maxLength} characters`
            );
          }
          fieldSchema = textareaSchema;
        } else {
          let textareaSchema = z.string();
          if (question.textConfig?.maxLength) {
            textareaSchema = textareaSchema.max(
              question.textConfig.maxLength,
              `Maximum length is ${question.textConfig.maxLength} characters`
            );
          }
          fieldSchema = textareaSchema.optional().or(z.literal(""));
        }
        break;
      default:
        fieldSchema = z.string();
    }

    schemaFields[question.name] = fieldSchema;
  });

  return z.object(schemaFields);
};

export function SignifyInterestStepTwo({
  customQuestions,
  onSubmit,
  onBack,
}: SignifyInterestStepTwoProps) {
  const posthog = usePostHog();
  const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;
  // Sort questions by order
  const sortedQuestions = [...customQuestions].sort(
    (a, b) => a.order - b.order
  );

  // Generate dynamic schema
  const dynamicSchema = generateSchema(sortedQuestions);
  type FormData = z.infer<typeof dynamicSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(dynamicSchema),
    mode: "onBlur",
  });

  const [filePreviews, setFilePreviews] = useState<
    Record<string, Array<{ name: string; url: string }>>
  >({});
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>(
    {}
  );
  const [fileData, setFileData] = useState<
    Record<string, Array<{ url: string; name: string }>>
  >({});

  const { mutateAsync: uploadFileAsync } = useUploadFileMutation(
    () => {
      // Success handled in handleFileChange
    },
    (error: any) => {
      showToast("error", "Failed to upload file. Please try again.");
      console.error("Upload error:", error);
    }
  );

  const handleFileChange = async (
    question: CustomQuestion,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const questionName = question.name;
    const selectedFiles = Array.from(e.target.files ?? []);
    if (selectedFiles.length === 0) return;

    const maxFiles = question.fileConfig?.maxFiles || 1;
    const allowedFormats = (question.fileConfig?.allowedFormats || []).map(
      (format) => format.toLowerCase().replace(/^\./, "")
    );

    if (selectedFiles.length > maxFiles) {
      showToast(
        "error",
        `You can upload up to ${maxFiles} file${maxFiles > 1 ? "s" : ""}.`
      );
    }

    const limitedFiles = selectedFiles.slice(0, maxFiles);
    const validFiles =
      allowedFormats.length === 0
        ? limitedFiles
        : limitedFiles.filter((file) => {
            const extension = file.name.split(".").pop()?.toLowerCase();
            return extension ? allowedFormats.includes(extension) : false;
          });

    if (allowedFormats.length > 0 && validFiles.length !== limitedFiles.length) {
      showToast("error", "Some files have unsupported formats.");
    }

    const sizeValidFiles = validFiles.filter(
      (file) => file.size <= MAX_FILE_SIZE_BYTES
    );

    if (validFiles.length !== sizeValidFiles.length) {
      showToast("error", "Some files exceed the 3MB size limit.");
    }

    if (sizeValidFiles.length === 0) {
      e.target.value = "";
      return;
    }

    const existingFiles = fileData[questionName] ?? [];
    const slotsLeft = maxFiles - existingFiles.length;
    if (slotsLeft <= 0) {
      showToast("error", `Maximum ${maxFiles} file(s) allowed. Remove one to add more.`);
      e.target.value = "";
      return;
    }

    const filesToUpload = sizeValidFiles.slice(0, slotsLeft);
    if (sizeValidFiles.length > slotsLeft) {
      showToast("success", `Only ${slotsLeft} more file(s) allowed. Added first ${slotsLeft}.`);
    }

    // Set uploading state
    setUploadingFiles((prev) => ({ ...prev, [questionName]: true }));
    setFileNames((prev) => ({
      ...prev,
      [questionName]: [...(fileNames[questionName] ?? []), ...filesToUpload.map((f) => f.name)],
    }));

    // Create previews for new images only
    const previewResults = await Promise.all(
      filesToUpload.map((file) => {
        if (!file.type.startsWith("image/")) return Promise.resolve(null);
        return new Promise<{ name: string; url: string }>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve({ name: file.name, url: reader.result as string });
          reader.readAsDataURL(file);
        });
      })
    );
    const newPreviews = previewResults.filter(
      (preview): preview is { name: string; url: string } => Boolean(preview)
    );
    setFilePreviews((prev) => ({
      ...prev,
      [questionName]: [...(prev[questionName] ?? []), ...newPreviews],
    }));

    try {
      const newlyUploaded: Array<{ url: string; name: string }> = [];

      for (const file of filesToUpload) {
        const response = await uploadFileAsync({
          file,
          name: file.name,
        });

        if (response?.url) {
          newlyUploaded.push({
            url: response.url,
            name: response.name || file.name,
          });
        } else {
          throw new Error("No URL returned from upload");
        }
      }

      const allFiles = [...existingFiles, ...newlyUploaded];
      setValue(
        questionName,
        allFiles.map((item) => item.url) as any,
        { shouldValidate: true }
      );
      setFileData((prev) => ({
        ...prev,
        [questionName]: allFiles,
      }));
      showToast(
        "success",
        newlyUploaded.length > 1
          ? "Files added successfully"
          : "File added successfully"
      );
    } catch (error) {
      // Revert fileNames to remove the names we were adding (keep existing)
      setFileNames((prev) => ({
        ...prev,
        [questionName]: (prev[questionName] ?? []).slice(
          0,
          (prev[questionName]?.length ?? 0) - filesToUpload.length
        ),
      }));
      setFilePreviews((prev) => ({
        ...prev,
        [questionName]: (prev[questionName] ?? []).slice(
          0,
          (prev[questionName]?.length ?? 0) - newPreviews.length
        ),
      }));
      // fileData and setValue stay at existing (no new files added)
      e.target.value = "";
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [questionName]: false }));
      e.target.value = "";
    }
  };

  const removeFile = (questionName: string, index: number) => {
    const current = fileData[questionName] ?? [];
    const removed = current[index];
    const newFiles = current.filter((_, i) => i !== index);
    setFileData((prev) => ({ ...prev, [questionName]: newFiles }));
    setValue(questionName, newFiles.map((f) => f.url) as any, {
      shouldValidate: true,
    });
    setFileNames((prev) => ({
      ...prev,
      [questionName]: (prev[questionName] ?? []).filter((_, i) => i !== index),
    }));
    if (removed?.name) {
      setFilePreviews((prev) => ({
        ...prev,
        [questionName]: (prev[questionName] ?? []).filter(
          (p) => p.name !== removed.name
        ),
      }));
    }
  };

  const handleFormSubmit = (data: FormData) => {
    const transformedData: Record<string, any> = { ...data };

    Object.keys(fileData).forEach((questionName) => {
      if (transformedData[questionName]) {
        transformedData[questionName] = fileData[questionName];
      }
    });

    posthog?.capture('form_submitted', { form_name: 'signify_interest_step_two', success: true });
    onSubmit(transformedData);
  };

  const renderQuestion = (question: CustomQuestion) => {
    const fieldName = question.name;
    const isRequired = question.required;
    const error = errors[fieldName];

    switch (question.type) {
      case "singleselect":
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <Select
              onValueChange={(value) => setValue(fieldName, value)}
              required={isRequired}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={question.placeholder || "Select an option"}
                />
              </SelectTrigger>
              <SelectContent>
                {question.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      case "multiselect":
        const selectedValues = (watch(fieldName) as string[]) || [];
        const toggleOption = (value: string) => {
          // Use getValues to get current value without causing re-render
          const currentValues = (getValues(fieldName) as string[]) || [];
          if (currentValues.includes(value)) {
            setValue(
              fieldName,
              currentValues.filter((v) => v !== value) as any,
              { shouldValidate: true }
            );
          } else {
            setValue(fieldName, [...currentValues, value] as any, {
              shouldValidate: true,
            });
          }
        };
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <div className="space-y-2 border border-input rounded-lg p-4 bg-muted/30">
              {question.options?.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleOption(option.value)}
                    />
                    <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {selectedValues.map((value) => {
                  const option = question.options?.find(
                    (opt) => opt.value === value
                  );
                  return (
                    <Badge key={value} variant="secondary" className="gap-1">
                      {option?.label || value}
                      <button
                        type="button"
                        onClick={() => toggleOption(value)}
                        className="ml-1 hover:opacity-80"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      case "url":
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <Input
              {...register(fieldName)}
              type="url"
              placeholder={question.placeholder || "Enter URL"}
              className="w-full"
            />
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      case "file":
        const isUploading = uploadingFiles[fieldName];
        const maxFiles = question.fileConfig?.maxFiles || 1;
        const allowedFormats = question.fileConfig?.allowedFormats || [];
        const currentFileList = fileData[fieldName] ?? [];
        const canAddMore = currentFileList.length < maxFiles;
        const acceptFormats =
          allowedFormats.length
            ? allowedFormats
                .map((format) => `.${format.replace(/^\./, "")}`)
                .join(",")
            : "*/*";
        const previewMap = new Map(
          (filePreviews[fieldName] ?? []).map((p) => [p.name, p.url])
        );
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <div className="rounded-lg border border-border bg-muted/30 p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">
                  File Upload Requirements
                </p>
                <span className={`text-xs font-medium px-2 py-1 rounded ${
                  currentFileList.length > 0 
                    ? "text-blue-600 bg-blue-50" 
                    : "text-muted-foreground bg-muted"
                }`}>
                  {currentFileList.length} / {maxFiles} file{maxFiles > 1 ? "s" : ""} uploaded
                </span>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>
                  <span className="font-medium">Max file size:</span> 3MB
                </p>
                {allowedFormats.length > 0 && (
                  <p>
                    <span className="font-medium">Allowed formats:</span> {allowedFormats.join(", ")}
                  </p>
                )}
                <p>
                  <span className="font-medium">Max files:</span> {maxFiles} file{maxFiles > 1 ? "s" : ""}
                </p>
              </div>
            </div>
            {currentFileList.length > 0 && (
              <div className="space-y-2 mb-3">
                {currentFileList.map((file, idx) => {
                  const previewUrl = previewMap.get(file.name);
                  return (
                    <div
                      key={`${file.url}-${idx}`}
                      className="flex items-center gap-3 rounded-lg border border-input bg-muted/30 p-3"
                    >
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={file.name}
                          className="h-12 w-12 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded bg-muted flex items-center justify-center shrink-0">
                          <FileIcon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <span className="text-sm font-medium truncate flex-1 min-w-0">
                        {file.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => removeFile(fieldName, idx)}
                        disabled={isUploading}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            {canAddMore && (
              <label
                className={`flex items-center justify-center w-full p-4 border border-border rounded-lg bg-muted transition-colors ${
                  isUploading
                    ? "cursor-wait opacity-60"
                    : "cursor-pointer hover:bg-muted/80"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">
                    {isUploading
                      ? "Uploading..."
                      : currentFileList.length > 0
                        ? "Add more files"
                        : maxFiles > 1
                          ? "Add files"
                          : "Add file"}
                  </span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept={acceptFormats}
                  multiple={maxFiles > 1}
                  onChange={(e) => handleFileChange(question, e)}
                  disabled={isUploading}
                />
              </label>
            )}
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      case "number":
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <Input
              {...register(fieldName, { valueAsNumber: true })}
              type="number"
              min={question.numberConfig?.min}
              max={question.numberConfig?.max}
              placeholder={question.placeholder || "Enter number"}
              className="w-full"
            />
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      case "text":
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <Input
              {...register(fieldName)}
              type="text"
              placeholder={question.placeholder || "Enter text"}
              maxLength={question.textConfig?.maxLength}
              className="w-full"
            />
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={fieldName}>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {question.label}
              {isRequired && <span className="text-red-500">*</span>}
            </label>
            <Textarea
              {...register(fieldName)}
              placeholder={question.placeholder || "Enter text"}
              rows={6}
              maxLength={question.textConfig?.maxLength}
              className="w-full"
            />
            {question.helpText && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.helpText}
              </p>
            )}
            {error && (
              <p className="text-red-500 text-sm mt-1">
                {error.message as string}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (sortedQuestions.length === 0) {
    return (
      <>
        <div>
          <h1 className="text-2xl font-bold">Additional Information</h1>
          <p className="mt-2 text-gray-600">
            No additional questions required.
          </p>
        </div>
        <div className="flex gap-4 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50 bg-transparent"
            trackingEvent='signify_interest_back'
            trackingProperties={{ step: 2 }}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => {
              posthog?.capture('form_submitted', { form_name: 'signify_interest_step_two', success: true });
              onSubmit({});
            }}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            trackingEvent='signify_interest_submitted'
            trackingProperties={{ step: 2 }}
          >
            Submit
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold">Additional Information</h1>
        <p className="mt-2 text-gray-600">
          Please answer the following questions to complete your application.
        </p>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
        {sortedQuestions.map((question) => renderQuestion(question))}

        <div className="flex gap-4 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50 bg-transparent"
            trackingEvent='signify_interest_back'
            trackingProperties={{ step: 2 }}
          >
            Back
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            trackingEvent='signify_interest_submitted'
            trackingProperties={{ step: 2 }}
          >
            Submit
          </Button>
        </div>
      </form>
    </>
  );
}
